import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, ArrowRight, Laptop } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

function Logo({ compact }) {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0 group">
      <Laptop size={compact ? 22 : 26} className="text-brand" strokeWidth={2.4} />
      <span className="leading-none">
        <span className="block font-poster text-white tracking-wide" style={{ fontSize: compact ? "1.05rem" : "1.2rem" }}>
          LUCRATIVE<span className="text-brand">LAPTOP</span>
        </span>
      </span>
    </Link>
  );
}

export default function Header() {
  const { settings } = useSettings();
  const nav = settings.nav || [];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setSearchOpen(false); }, [location.pathname]);

  const submitSearch = (e) => { e.preventDefault(); if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`); };
  const isActive = (link) => location.pathname === link || (link !== "/" && location.pathname.startsWith(link));

  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 border-b transition-all duration-300 ${scrolled ? "bg-ink-950/95 backdrop-blur-md border-ink-600" : "bg-transparent border-transparent"}`}>
      <div className="max-w-[1600px] mx-auto px-5 lg:px-10">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-[62px]" : "h-[80px]"}`}>
          <Logo compact={scrolled} />

          <nav className="hidden lg:flex items-center gap-6">
            {nav.map((item) => (
              <Link key={item.label} to={item.link} data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`nav-underline font-cond font-bold uppercase tracking-[0.08em] text-[15px] transition-colors ${isActive(item.link) ? "active text-brand" : "text-white/85 hover:text-white"}`}>
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button data-testid="search-toggle" onClick={() => setSearchOpen((v) => !v)} className="p-2 text-white/85 hover:text-brand transition-colors" aria-label="Search"><Search size={19} /></button>
            <Link to={settings.start_here_link || "/start-here"} data-testid="start-here-button" className="hidden sm:inline-flex btn-poster text-sm py-2.5 px-5">
              <span>START HERE</span><ArrowRight size={16} className="relative z-10" />
            </Link>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 text-white" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-4">
            <div className="flex items-center gap-2 bg-ink-800 border border-ink-600 px-4 py-3">
              <Search size={16} className="text-brand" />
              <input autoFocus data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="SEARCH IDEAS, TOOLS, STORIES" className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-white/40 font-cond uppercase tracking-wider" />
            </div>
          </form>
        )}
      </div>

      {open && (
        <div className="lg:hidden bg-ink-950 border-t border-ink-600 px-5 py-4" data-testid="mobile-menu">
          <div className="flex flex-col">
            {nav.map((item) => (
              <Link key={item.label} to={item.link} className={`py-3.5 font-cond font-bold uppercase tracking-wider text-lg border-b border-ink-700 ${isActive(item.link) ? "text-brand" : "text-white/90"}`}>{item.label}</Link>
            ))}
            <Link to={settings.start_here_link || "/start-here"} className="mt-5 btn-poster justify-center"><span>START HERE</span><ArrowRight size={16} className="relative z-10" /></Link>
          </div>
        </div>
      )}
    </header>
  );
}
